import { useState, useEffect } from "react";
import { Button } from "@app/components/shared/Button";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import {
  getSubscriberPositions,
  executePositionAction,
  type SubscriberPositionRow,
  type PositionAction,
  type PositionStatus,
  type SubscriberSourceFilter,
  STATUS_LABELS,
  STATUS_COLORS,
  ACTION_LABELS,
  ACTION_COLORS,
  SOURCE_LABELS,
  SOURCE_COLORS,
  SOURCE_FILTER_LABELS,
} from "@app/services/adminPositionsMonitorApi";

const VALUE_FILTERS: { label: string; value: number | null }[] = [
  { label: "All Values", value: null },
  { label: "> $1", value: 1 },
  { label: "> $5", value: 5 },
  { label: "> $10", value: 10 },
  { label: "> $100", value: 100 },
];

export function SubscriberPositionsTab(): JSX.Element {
  const [data, setData] = useState<SubscriberPositionRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [symbolFilter, setSymbolFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState<SubscriberSourceFilter>("all");
  const [valueFilter, setValueFilter] = useState<number | null>(null);
  const [emailFilter, setEmailFilter] = useState("");
  const [executingAction, setExecutingAction] = useState<{
    positionId: string;
    action: PositionAction;
  } | null>(null);
  const [actionMessage, setActionMessage] = useState<{
    text: string;
    type: "success" | "error";
  } | null>(null);

  const actionVariantMap: Record<string, "primary" | "danger" | "secondary" | "accent" | "neutral"> = {
    primary: "primary",
    danger: "danger",
    warning: "secondary",
    success: "accent",
    secondary: "secondary",
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {};
      if (symbolFilter) params.symbol = symbolFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (valueFilter !== null) params.min_value = valueFilter;
      if (emailFilter) params.user_email = emailFilter;

      const result = await getSubscriberPositions(params);
      setData(result.positions);
      setTotalCount(result.total_count);
    } catch (err: any) {
      setError(err.message || "Failed to fetch subscriber positions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (positionId: string, action: PositionAction) => {
    setExecutingAction({ positionId, action });
    setActionMessage(null);
    try {
      const result = await executePositionAction(positionId, action);
      setActionMessage({ text: result.message, type: "success" });
      await fetchData();
    } catch (err: any) {
      setActionMessage({ text: err.message || "Action failed", type: "error" });
    } finally {
      setExecutingAction(null);
    }
  };

  const statusBadge = (status: PositionStatus) => (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "0.75rem",
        fontWeight: 600,
        backgroundColor: STATUS_COLORS[status] + "20",
        color: STATUS_COLORS[status],
        border: `1px solid ${STATUS_COLORS[status]}40`,
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  );

  const sourceBadge = (source: string) => (
    <span
      key={source}
      style={{
        display: "inline-block",
        padding: "1px 6px",
        borderRadius: "3px",
        fontSize: "0.65rem",
        fontWeight: 500,
        backgroundColor: SOURCE_COLORS[source] + "20",
        color: SOURCE_COLORS[source],
        marginRight: "4px",
        marginBottom: "2px",
      }}
    >
      {SOURCE_LABELS[source] || source}
    </span>
  );

  const formatUsd = (value: number) =>
    value >= 1000
      ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
      : `$${value.toFixed(value >= 1 ? 2 : 4)}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <SectionHeader
        title="Subscriber Positions"
        subtitle={`${totalCount} position${totalCount !== 1 ? "s" : ""} found • Clean up stuck subscriber positions from all sources`}
      />

      <div
        style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          alignItems: "flex-end",
          padding: "12px",
          backgroundColor: "var(--bg-card)",
          borderRadius: "8px",
          border: "1px solid var(--border-color)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: "120px" }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-secondary)" }}>
            Symbol
          </label>
          <input
            type="text"
            placeholder="e.g. WAL"
            value={symbolFilter}
            onChange={(e) => setSymbolFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchData()}
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              backgroundColor: "var(--bg-input)",
              color: "var(--text-primary)",
              fontSize: "0.85rem",
              width: "100px",
            }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: "130px" }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-secondary)" }}>
            Data Source
          </label>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as SubscriberSourceFilter)}
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              backgroundColor: "var(--bg-input)",
              color: "var(--text-primary)",
              fontSize: "0.85rem",
            }}
          >
            {Object.entries(SOURCE_FILTER_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: "110px" }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-secondary)" }}>
            Notional Value
          </label>
          <select
            value={valueFilter ?? ""}
            onChange={(e) =>
              setValueFilter(e.target.value === "" ? null : Number(e.target.value))
            }
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              backgroundColor: "var(--bg-input)",
              color: "var(--text-primary)",
              fontSize: "0.85rem",
            }}
          >
            {VALUE_FILTERS.map((f) => (
              <option key={f.label} value={f.value ?? ""}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: "160px" }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-secondary)" }}>
            User Email
          </label>
          <input
            type="text"
            placeholder="user@email.com"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchData()}
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid var(--border-color)",
              backgroundColor: "var(--bg-input)",
              color: "var(--text-primary)",
              fontSize: "0.85rem",
              width: "150px",
            }}
          />
        </div>

        <Button variant="primary" onClick={fetchData} disabled={isLoading}>
          {isLoading ? "Loading..." : "Search"}
        </Button>
      </div>

      {actionMessage && (
        <div
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            backgroundColor:
              actionMessage.type === "success"
                ? "var(--color-success-bg, #d4edda)"
                : "var(--color-error-bg, #f8d7da)",
            color:
              actionMessage.type === "success"
                ? "var(--color-success-text, #155724)"
                : "var(--color-error-text, #721c24)",
            fontSize: "0.85rem",
            fontWeight: 500,
          }}
        >
          {actionMessage.text}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "6px",
            backgroundColor: "var(--color-error-bg, #f8d7da)",
            color: "var(--color-error-text, #721c24)",
            fontSize: "0.85rem",
          }}
        >
          {error}
        </div>
      )}

      {!isLoading && data.length === 0 && !error && (
        <div
          style={{
            padding: "24px",
            textAlign: "center",
            color: "var(--text-secondary)",
            fontSize: "0.9rem",
          }}
        >
          No subscriber positions found. Try adjusting filters or use the Search button.
        </div>
      )}

      {data.length > 0 && (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table
            style={{
              width: "100%",
              minWidth: "1100px",
              borderCollapse: "separate",
              borderSpacing: "0",
              fontSize: "0.85rem",
              tableLayout: "auto",
            }}
          >
            <colgroup>
              <col style={{ minWidth: "200px" }} />
              <col style={{ minWidth: "100px" }} />
              <col style={{ minWidth: "90px" }} />
              <col style={{ minWidth: "90px" }} />
              <col style={{ minWidth: "110px" }} />
              <col style={{ minWidth: "90px" }} />
              <col style={{ minWidth: "170px" }} />
              <col style={{ minWidth: "90px" }} />
              <col style={{ minWidth: "320px" }} />
            </colgroup>
            <thead>
              <tr
                style={{
                  backgroundColor: "var(--bg-card-secondary)",
                }}
              >
                <th style={{ ...thStyle, borderTopLeftRadius: "8px" }}>User</th>
                <th style={thStyle}>Symbol</th>
                <th style={thStyle}>Entry Price</th>
                <th style={thStyle}>Qty</th>
                <th style={thStyle}>Notional Value</th>
                <th style={thStyle}>Opened By</th>
                <th style={thStyle}>Sources</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, borderTopRightRadius: "8px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr
                  key={`${row.position_id}-${idx}`}
                  style={{
                    backgroundColor: idx % 2 === 0 ? "transparent" : "var(--bg-card-secondary)",
                    borderBottom: "1px solid var(--border-color)",
                  }}
                >
                  <td style={tdStyle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                        {row.owner_email}
                      </span>
                      {row.account_nickname && (
                        <span style={{ fontSize: "0.7rem", color: "var(--text-secondary)" }}>
                          {row.account_nickname}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: "var(--text-primary)" }}>
                    {row.symbol}
                  </td>
                  <td style={{ ...tdStyle, fontVariantNumeric: "tabular-nums" }}>
                    {formatUsd(row.entry_price)}
                  </td>
                  <td style={{ ...tdStyle, fontVariantNumeric: "tabular-nums" }}>
                    {row.quantity.toFixed(row.quantity >= 100 ? 1 : 4)}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatUsd(row.notional_value)}
                  </td>
                  <td style={tdStyle}>
                    {(() => {
                      const badgeStyle = (bg: string, c: string) => ({
                        display: "inline-block",
                        padding: "2px 10px",
                        borderRadius: "10px",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        textTransform: "uppercase" as const,
                        backgroundColor: bg,
                        color: c,
                      });
                      if (row.opened_by === "system")
                        return <span style={badgeStyle("#d4edda", "#155724")}>system</span>;
                      if (row.opened_by === "user")
                        return <span style={badgeStyle("#fff3cd", "#856404")}>user</span>;
                      return <span style={badgeStyle("#f1f5f9", "#64748b")}>unknown</span>;
                    })()}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "3px" }}>
                      {row.data_sources.map((s) => sourceBadge(s))}
                    </div>
                  </td>
                  <td style={tdStyle}>{statusBadge(row.status)}</td>
                  <td style={{ ...tdStyle, minWidth: "280px" }}>
                    <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                      {row.available_actions.map((action) => {
                        const isExecuting =
                          executingAction?.positionId === row.position_id &&
                          executingAction?.action === action;
                        return (
                          <Button
                            key={action}
                            variant={actionVariantMap[ACTION_COLORS[action]] || "secondary"}
                            size="sm"
                            onClick={() => handleAction(row.position_id, action)}
                            disabled={isExecuting}
                            title={ACTION_LABELS[action]}
                            style={{ fontSize: "0.72rem", padding: "3px 10px" }}
                          >
                            {isExecuting ? "..." : ACTION_LABELS[action]}
                          </Button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: "12px 16px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "0.8rem",
  color: "var(--text-secondary)",
  whiteSpace: "nowrap",
  borderBottom: "2px solid var(--border-color)",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 16px",
  fontSize: "0.85rem",
  whiteSpace: "nowrap",
  color: "var(--text-primary)",
};
